import { useLiveQuery, usePendingMutationCount, useStore, useSyncStatus } from "@hq/client-react";
import type { PaneComponentProps } from "@hq/layout";
import { ExportVaultButton } from "@hq/notes-ui";
import { Button, Dialog, Select, toast } from "@z/ui";
import { useState } from "react";
import { useAppConfig } from "../AppConfig";
import { KeyboardSettings } from "./KeyboardSettings";
import { AccountSettings } from "./AccountSettings";
import { useAccountSnapshot } from "../auth/useAccountSnapshot";
import { THEME_KV_KEY, type ThemePreference } from "../theme";

function Row(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border/60 py-3">
      <span className="text-fg-muted text-sm">{props.label}</span>
      <span className="min-w-0 break-all text-sm">{props.children}</span>
    </div>
  );
}

/** Theme, sync diagnostics (research.md Section 27), export, and reset. */
export function SettingsPane(_props: PaneComponentProps) {
  const store = useStore();
  const config = useAppConfig();
  const status = useSyncStatus();
  const account = useAccountSnapshot(config.account);
  const pending = usePendingMutationCount().data ?? 0;
  const { data: theme } = useLiveQuery(["kv"], (s) => s.kv.get<ThemePreference>(THEME_KV_KEY), []);
  const { data: syncState } = useLiveQuery(["sync_state"], (s) => s.sync.get(), []);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const reset = async () => {
    if (!config.onResetLocalData || (config.hasServer && pending > 0)) return;
    setResetting(true);
    try {
      await config.onResetLocalData();
    } catch (err) {
      setResetting(false);
      setConfirmReset(false);
      toast({
        title: "Could not reset",
        description: err instanceof Error ? err.message : String(err),
        variant: "danger",
      });
    }
  };

  return (
    <div className="mx-auto h-full max-w-xl overflow-y-auto p-6">
      <h1 className="mb-6 text-xl font-semibold tracking-tight">Settings</h1>

      <section className="mb-6">
        <h2 className="text-fg mb-2 text-sm font-medium">Appearance</h2>
        <Row label="Theme">
          <Select.Root
            value={theme ?? "system"}
            onValueChange={(v) => void store.kv.set(THEME_KV_KEY, v as ThemePreference)}
          >
            <Select.Trigger size="sm" placeholder="System" />
            <Select.Content>
              <Select.Item value="system">System</Select.Item>
              <Select.Item value="light">Light</Select.Item>
              <Select.Item value="dark">Dark</Select.Item>
            </Select.Content>
          </Select.Root>
        </Row>
      </section>

      <KeyboardSettings />

      <section className="mb-6">
        <h2 className="text-fg mb-2 text-sm font-medium">Sync</h2>
        <Row label="Server">{config.hasServer ? "Configured" : "None (local only)"}</Row>
        <Row label="Status">
          {config.hasServer
            ? account.phase === "signed_in"
              ? status.updateRequired
                ? "Update required"
                : status.phase
              : "Sign in to sync"
            : "Saved on this device"}
        </Row>
        {!config.hasServer && (
          <p className="pt-3 text-sm text-fg-muted">
            Your workspace is stored in this browser. Sync is not connected.
          </p>
        )}
        <AccountSettings />
        <details className="mt-3 text-sm text-fg-muted">
          <summary className="cursor-pointer rounded-sm focus-ring">Technical details</summary>
          <Row
            label={config.hasServer ? "Pending changes" : "Local changes retained for future sync"}
          >
            {pending}
          </Row>
          <Row label="Last push">{status.lastPushAt ?? "never"}</Row>
          <Row label="Last pull">{status.lastPullAt ?? "never"}</Row>
          <Row label="Cursor">{status.cursor}</Row>
          <Row label="Device">
            <code className="font-mono text-xs">{syncState?.deviceId ?? "…"}</code>
          </Row>
          {config.hasServer && status.lastError ? (
            <Row label="Last error">
              <span className="text-danger">{status.lastError}</span>
            </Row>
          ) : null}
        </details>
      </section>

      <section className="mb-6">
        <h2 className="text-fg mb-2 text-sm font-medium">Data</h2>
        <Row label="Export all notes as Markdown">
          <ExportVaultButton />
        </Row>
        <Row label="Local database boot">
          {config.bootMs !== undefined ? `${config.bootMs} ms` : "n/a"}
        </Row>
        {config.onResetLocalData ? (
          <Row label="Reset local data">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmReset(true)}
              disabled={config.hasServer && pending > 0}
            >
              Reset…
            </Button>
          </Row>
        ) : null}
        {config.hasServer && pending > 0 ? (
          <p className="text-fg-faint text-xs">
            Reset is disabled while there are unsynced changes.
          </p>
        ) : null}
      </section>

      <Dialog.Root open={confirmReset} onOpenChange={setConfirmReset}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Reset local data?</Dialog.Title>
            <Dialog.Description>
              This permanently deletes all notes, tasks, folders, projects, and workspace settings
              in this browser. Export anything you want to keep first. This cannot be undone.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button variant="secondary" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void reset()} disabled={resetting}>
              {resetting ? "Resetting…" : "Delete everything"}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
